
/* eslint-disable indent */

import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

import {
    getImports,
    createImport,
    getExports,
    createExport,
    downloadExport,
} from '../../api/imports';

import { getParcels } from '../../api/parcels';

import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import { Select } from '../../components/common/Input';
import { FullPageSpinner } from '../../components/common/Spinner';

const statusColor = (s) => {
    if (!s) return 'gray';

    const st = s.toLowerCase();

    if (st === 'completed') return 'green';
    if (st === 'failed') return 'red';
    if (st === 'processing' || st === 'pending') return 'yellow';

    return 'gray';
};

const EXPORT_TYPES = [
    { value: 'measurements', label: 'Мерења' },
    { value: 'parcels', label: 'Парцели' },
];

export default function ImportsExports() {
    const [imports, setImports] = useState([]);
    const [exports, setExports] = useState([]);
    const [parcels, setParcels] = useState([]);

    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [exportType, setExportType] = useState('measurements');
    const [selectedParcel, setSelectedParcel] = useState('');

    const fileRef = useRef(null);

    useEffect(() => {
        Promise.all([getImports(), getExports(), getParcels()])
            .then(([iRes, eRes, pRes]) => {
                const parcelList = pRes.data?.results ?? pRes.data;

                setImports(iRes.data?.results ?? iRes.data);
                setExports(eRes.data?.results ?? eRes.data);
                setParcels(parcelList);

                if (parcelList.length > 0) {
                    setSelectedParcel(String(parcelList[0].id));
                }
            })
            .catch(() => toast.error('Неуспешно вчитување на податоците'))
            .finally(() => setLoading(false));
    }, []);

    const handleImport = async (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        setUploading(true);

        const formData = new FormData();

        formData.append('file', file);
        formData.append('file_name', file.name);
        formData.append(
            'file_type',
            file.name.endsWith('.json') ? 'json' : 'csv'
        );

        try {
            const res = await createImport(formData);

            setImports((prev) => [res.data, ...prev]);

            toast.success('Датотеката е успешно прикачена');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Импортирањето не успеа');
        } finally {
            setUploading(false);

            if (fileRef.current) {
                fileRef.current.value = '';
            }
        }
    };

    const handleExport = async (e) => {
        e.preventDefault();

        if (exportType === 'measurements' && !selectedParcel) {
            toast.error('Прво изберете парцела');
            return;
        }

        setExporting(true);

        try {
            const payload = {
                export_type: exportType,
            };

            if (exportType === 'measurements') {
                payload.parcel = selectedParcel;
            }

            const res = await createExport(payload);

            setExports((prev) => [res.data, ...prev]);

            toast.success('Барањето за импортирање е создадено');
        } catch (err) {
            const msg = err.response?.data
                ? Object.values(err.response.data).flat().join(' ')
                : 'Импортирањето не успеа';

            toast.error(msg);
        } finally {
            setExporting(false);
        }
    };

    const handleDownload = async (exp) => {
        try {
            const res = await downloadExport(exp.id);

            const blob = new Blob([res.data], {
                type: 'application/pdf',
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');

            link.href = url;
            link.download = `${exp.export_type}_export_${exp.id}.pdf`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Преземањето не успеа');
        }
    };

    if (loading) return <FullPageSpinner />;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-800">
                    Импортирање / Експортирање
                </h1>

                <p className="text-gray-500 text-sm mt-1">
                    Прикачете датотеки или побарајте експорт на податоци
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <h2 className="font-semibold text-gray-700 mb-3">
                        Импорт на податоци
                    </h2>

                    <p className="text-sm text-gray-500 mb-4">
                        Прикачете CSV или JSON датотека за увоз на записи.
                    </p>

                    <div
                        className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-green-400 transition-colors cursor-pointer"
                        onClick={() => fileRef.current?.click()}
                    >
                        <svg
                            className="w-10 h-10 text-gray-300 mx-auto mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                            />
                        </svg>

                        <p className="text-sm text-gray-500">
                            Кликнете за да изберете датотека
                        </p>

                        <p className="text-xs text-gray-400 mt-1">
                            CSV или JSON
                        </p>
                    </div>

                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,.json"
                        onChange={handleImport}
                        className="hidden"
                    />

                    {uploading && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                            <span className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                            Прикачување...
                        </div>
                    )}
                </Card>

                <Card>
                    <h2 className="font-semibold text-gray-700 mb-3">
                        Експорт на податоци
                    </h2>

                    <p className="text-sm text-gray-500 mb-4">
                        Побарајте експорт на податоци — системот ќе ја генерира датотеката.
                    </p>

                    <form onSubmit={handleExport} className="space-y-4">
                        <Select
                            label="Вид на податоци"
                            value={exportType}
                            onChange={(e) => setExportType(e.target.value)}
                        >
                            {EXPORT_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </Select>

                        {exportType === 'measurements' && (
                            <Select
                                label="Парцела"
                                value={selectedParcel}
                                onChange={(e) =>
                                    setSelectedParcel(e.target.value)
                                }
                            >
                                {parcels.map((p) => (
                                    <option key={p.id} value={String(p.id)}>
                                        {p.parcel_name}
                                    </option>
                                ))}
                            </Select>
                        )}

                        <Button
                            type="submit"
                            loading={exporting}
                            className="w-full"
                        >
                            Побарај експорт
                        </Button>
                    </form>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-700">
                            Историја на импорт
                        </h2>
                    </div>

                    {imports.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm py-10">
                            Сè уште нема импорти.
                        </p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Датотека', 'Тип', 'Датум', 'Статус', 'Редови'].map(
                                        (h) => (
                                            <th
                                                key={h}
                                                className="text-left px-5 py-3 text-gray-500 font-medium"
                                            >
                                                {h}
                                            </th>
                                        )
                                    )}
                                </tr>
                            </thead>

                            <tbody>
                                {imports.map((imp) => (
                                    <tr
                                        key={imp.id}
                                        className="border-b border-gray-50 hover:bg-gray-50"
                                    >
                                        <td className="px-5 py-3 text-xs text-gray-700 truncate max-w-[140px]">
                                            {imp.file_name ||
                                                `Импорт #${imp.id}`}
                                        </td>

                                        <td className="px-5 py-3">
                                            <Badge color="gray">
                                                {(imp.file_type || '—').toUpperCase()}
                                            </Badge>
                                        </td>

                                        <td className="px-5 py-3 text-xs text-gray-500">
                                            {imp.uploaded_at
                                                ? format(
                                                      new Date(imp.uploaded_at),
                                                      'MMM dd, HH:mm'
                                                  )
                                                : '—'}
                                        </td>

                                        <td className="px-5 py-3">
                                            <Badge
                                                color={statusColor(imp.status)}
                                            >
                                                {imp.status || 'pending'}
                                            </Badge>
                                        </td>

                                        <td className="px-5 py-3 text-gray-500">
                                            {imp.row_count ?? '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-700">
                            Историја на експорт
                        </h2>
                    </div>

                    {exports.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm py-10">
                            Сè уште нема експорти.
                        </p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Тип', 'Датум', 'Статус', ''].map(
                                        (h, i) => (
                                            <th
                                                key={i}
                                                className="text-left px-5 py-3 text-gray-500 font-medium"
                                            >
                                                {h}
                                            </th>
                                        )
                                    )}
                                </tr>
                            </thead>

                            <tbody>
                                {exports.map((exp) => (
                                    <tr
                                        key={exp.id}
                                        className="border-b border-gray-50 hover:bg-gray-50"
                                    >
                                        <td className="px-5 py-3">
                                            <Badge color="blue">
                                                {EXPORT_TYPES.find(
                                                    (t) =>
                                                        t.value ===
                                                        exp.export_type
                                                )?.label ||
                                                    exp.export_type ||
                                                    '—'}
                                            </Badge>
                                        </td>

                                        <td className="px-5 py-3 text-xs text-gray-500">
                                            {exp.created_at
                                                ? format(
                                                      new Date(exp.created_at),
                                                      'MMM dd, HH:mm'
                                                  )
                                                : '—'}
                                        </td>

                                        <td className="px-5 py-3">
                                            <Badge
                                                color={statusColor(exp.status)}
                                            >
                                                {exp.status || 'pending'}
                                            </Badge>
                                        </td>

                                        <td className="px-5 py-3">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    handleDownload(exp)
                                                }
                                                disabled={
                                                    exp.status !== 'completed'
                                                }
                                                className="text-green-700 disabled:opacity-40"
                                            >
                                                Преземи
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
